import { z } from "zod";

export const ORGANIZATION_NAME_MAX_LENGTH = 100;
export const ORGANIZATION_DISPLAY_NAME_MAX_LENGTH = 100;
export const ORGANIZATION_KEY_MAX_LENGTH = 25;

/**
 * Coerces form / UI values to a native {@link File} for multipart upload.
 * Only a real browser File is returned — avoids appending non-File values that
 * break APIs expecting `instanceof File` or a binary part.
 */
export function normalizeOrganizationLogoFile(value: unknown): File | undefined {
  if (value == null) return undefined;
  if (value instanceof File) return value;
  if (typeof FileList !== "undefined" && value instanceof FileList) {
    return value.length > 0 ? (value.item(0) ?? value[0]) : undefined;
  }
  if (
    typeof value === "object" &&
    value !== null &&
    "originFileObj" in value &&
    (value as { originFileObj?: unknown }).originFileObj instanceof File
  ) {
    return (value as { originFileObj: File }).originFileObj;
  }
  return undefined;
}

/** Schema for get-organizations pagination + optional ids input. */
const GetOrganizationsSchema = z.object({
  page_number: z.number().int().min(1),
  page_size: z.number().int().min(1).max(100),
  organization_ids: z.array(z.number().int().positive()).optional(),
});

/** Create/update organization via REST multipart `/admin/organization` (camelCase fields). */
const UpdateOrganizationSchema = z.object({
  organizationId: z.number().int().positive().nullable().optional(),
  organizationKey: z
    .string()
    .trim()
    .min(1)
    .regex(/^[A-Z]+(-[A-Z]+)*$/, "Invalid organization key format"),
  organizationName: z
    .string()
    .trim()
    .min(1)
    .max(ORGANIZATION_NAME_MAX_LENGTH),
  organizationDisplayName: z
    .string()
    .trim()
    .min(1)
    .max(ORGANIZATION_DISPLAY_NAME_MAX_LENGTH),
  organizationDescription: z.string().max(200).optional().nullable(),
  organizationIsActive: z.boolean(),
  organizationLogoFile: z.preprocess(
    (val) => normalizeOrganizationLogoFile(val),
    z.instanceof(File).optional(),
  ),
  organizationLogoUrl: z.preprocess(
    (val) => {
      if (val == null || val === "") return undefined;
      return typeof val === "string" ? val.trim() : undefined;
    },
    z.string().max(2048).optional(),
  ),
  /** Edit + multipart: explicit clear when no new file (empty organizationLogoUrl). */
  organizationLogoRemove: z.boolean().optional(),
})
  .superRefine((data, ctx) => {
    if (data.organizationLogoFile && data.organizationLogoRemove) {
      ctx.addIssue({
        code: "custom",
        message: "Cannot remove the logo and upload a new file in the same save",
        path: ["organizationLogoRemove"],
      });
    }
    if (data.organizationLogoRemove && data.organizationLogoUrl?.trim()) {
      ctx.addIssue({
        code: "custom",
        message: "Cannot remove the logo and send a logo URL in the same save",
        path: ["organizationLogoUrl"],
      });
    }
  });

export type GetOrganizationsDTOShape = z.infer<typeof GetOrganizationsSchema>;
export type UpdateOrganizationDTOShape = z.infer<typeof UpdateOrganizationSchema>;

/**
 * GetOrganizationsDTO – core contract for "get organizations" input.
 * Use constructor when data is already valid; use validate() for untrusted input.
 */
export class GetOrganizationsDTO {
  readonly page_number!: number;
  readonly page_size!: number;
  readonly organization_ids?: number[];

  constructor(data: GetOrganizationsDTOShape) {
    const validated = GetOrganizationsSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return GetOrganizationsSchema.safeParse(data);
  }
}

export class UpdateOrganizationDTO {
  readonly organizationId?: number | null;
  readonly organizationKey!: string;
  readonly organizationName!: string;
  readonly organizationDisplayName!: string;
  readonly organizationDescription?: string | null;
  readonly organizationIsActive!: boolean;
  readonly organizationLogoFile?: File;
  readonly organizationLogoUrl?: string;
  readonly organizationLogoRemove?: boolean;

  constructor(data: UpdateOrganizationDTOShape) {
    const validated = UpdateOrganizationSchema.parse(data);
    Object.assign(this, validated);
  }

  static validate(data: unknown) {
    return UpdateOrganizationSchema.safeParse(data);
  }

  /** Maps validated DTO → REST multipart body (camelCase, same field names as GraphQL). */
  toMultipartFormData(): FormData {
    const formData = new FormData();
    formData.append("organizationKey", this.organizationKey);
    formData.append("organizationName", this.organizationName);
    formData.append("organizationDisplayName", this.organizationDisplayName);
    formData.append(
      "organizationDescription",
      this.organizationDescription?.trim() ?? "",
    );
    formData.append("isActive", this.organizationIsActive ? "true" : "false");

    if (this.organizationId != null && this.organizationId > 0) {
      formData.append("organizationId", String(this.organizationId));
    }

    const isEdit = this.organizationId != null && this.organizationId > 0;
    const logoFile = normalizeOrganizationLogoFile(this.organizationLogoFile);
    const trimmedLogoUrl = this.organizationLogoUrl?.trim() ?? "";
    const removeLogo = isEdit && this.organizationLogoRemove === true;

    if (logoFile instanceof File) {
      formData.append("organizationLogoUrl", logoFile, logoFile.name);
    } else if (removeLogo) {
      formData.append("organizationLogoUrl", "");
    } else if (trimmedLogoUrl.length > 0) {
      formData.append("organizationLogoUrl", trimmedLogoUrl);
    }

    return formData;
  }
}
